/**
 * Generic JSON-LD enrichment utility.
 *
 * Given a plain JSON object and a field→class mapping, recursively walks the
 * structure and injects `@type` (and `@id` where `identifier` exists) into
 * every object whose parent field is listed in the mapping.
 *
 * The mapping is the single source of truth — no per-field hardcoding.
 */

export type FieldTypeMapping = Record<string, string>;

/**
 * Recursively enrich a plain JSON object with JSON-LD `@type` and `@id`.
 *
 * For every key in `obj`:
 *  - If the key is in `mapping` and the value is an object → add `@type`, recurse into it
 *  - If the key is in `mapping` and the value is an array  → for each object element, add `@type` and recurse
 *  - For any object (mapped or not) that contains `identifier` → also add `@id`
 *  - Primitives and arrays of primitives pass through unchanged
 *  - Existing `@type` / `@id` are never overwritten
 */
export function enrichWithJsonLd(
  obj: Record<string, unknown>,
  mapping: FieldTypeMapping,
): Record<string, unknown> {
  const result: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(obj)) {
    result[key] = enrichField(key, value, mapping);
  }

  return result;
}

function enrichField(
  fieldName: string,
  value: unknown,
  mapping: FieldTypeMapping,
): unknown {
  if (value === null || value === undefined) return value;

  const mappedType = mapping[fieldName];

  if (Array.isArray(value)) {
    return value.map((item) => {
      if (item !== null && typeof item === "object" && !Array.isArray(item)) {
        return enrichObject(
          item as Record<string, unknown>,
          mappedType,
          mapping,
        );
      }
      return item;
    });
  }

  if (typeof value === "object") {
    return enrichObject(value as Record<string, unknown>, mappedType, mapping);
  }

  return value;
}

function enrichObject(
  obj: Record<string, unknown>,
  typeName: string | undefined,
  mapping: FieldTypeMapping,
): Record<string, unknown> {
  const enriched = enrichWithJsonLd(obj, mapping);

  const result: Record<string, unknown> = {};

  if (typeName && !enriched["@type"]) {
    result["@type"] = typeName;
  } else if (enriched["@type"]) {
    result["@type"] = enriched["@type"];
  }

  if (
    typeof enriched.identifier === "string" &&
    enriched.identifier &&
    !enriched["@id"]
  ) {
    result["@id"] = enriched.identifier;
  } else if (enriched["@id"]) {
    result["@id"] = enriched["@id"];
  }

  for (const [key, value] of Object.entries(enriched)) {
    if (key === "@type" || key === "@id") continue;
    result[key] = value;
  }

  return result;
}

/**
 * Recursively strip JSON-LD metadata (`@context`, `@type`, `@id`) from
 * an object tree so that the result can be loaded into the form without
 * interference from semantic fields.
 */
export function stripJsonLdMeta(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripJsonLdMeta);
  if (value !== null && typeof value === "object") {
    const result: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (k === "@context" || k === "@type" || k === "@id") continue;
      result[k] = stripJsonLdMeta(v);
    }
    return result;
  }
  return value;
}
